/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Live-duration sub-modal — Figma 3629:7114. A centered ~454-wide card that
// asks how long to share a live location before the share is emitted. Three
// radio options (15 minutes / 1 hour / 24 hours → 900 / 3600 / 86400s).
//
// Tailwind/NativeWind classes lead per the repo styling rule; exact Figma
// values use arbitrary-value classes (bg-[#15171C], rounded-[16px], …).

import React, { useState } from 'react';
import { Modal as RNModal, Pressable, Text, View } from 'react-native';
import { X } from 'lucide-react-native';
import { LIVE_DURATION_OPTIONS } from './types';
import { shareLocationStrings } from './strings';

export interface LiveDurationModalProps {
  readonly visible: boolean;
  /** Conversation name interpolated into the body copy. */
  readonly conversationName?: string | undefined;
  /** Initially-selected duration in seconds. */
  readonly defaultDurationSeconds?: number;
  readonly onCancel: () => void;
  /** Confirm — emits the chosen duration (seconds). */
  readonly onConfirm: (durationSeconds: number) => void;
}

export const LiveDurationModal: React.FC<LiveDurationModalProps> = ({
  visible,
  conversationName,
  defaultDurationSeconds = 60 * 60,
  onCancel,
  onConfirm,
}) => {
  const [selected, setSelected] = useState<number>(defaultDurationSeconds);

  const name =
    conversationName && conversationName.length > 0
      ? conversationName
      : shareLocationStrings.live.bodyFallback;

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
      accessibilityViewIsModal
    >
      <Pressable
        className="flex-1 items-center justify-center bg-black/60 p-md"
        onPress={onCancel}
      >
        <Pressable
          onPress={(event) => event.stopPropagation()}
          className="w-full max-w-[454px] rounded-2xl bg-white dark:bg-dark-surface-panel p-6 gap-5 border border-light-border-subtle dark:border-dark-border-subtle"
          accessibilityLabel={shareLocationStrings.live.title}
        >
          {/* Title row */}
          <View className="flex-row items-center justify-between">
            <Text className="text-[20px] font-bold text-white">
              {shareLocationStrings.live.title}
            </Text>
            <Pressable
              onPress={onCancel}
              accessibilityRole="button"
              accessibilityLabel={shareLocationStrings.modal.close}
              className="h-7 w-7 items-center justify-center rounded-[12px]"
            >
              <X size={20} color="#B5BAC3" />
            </Pressable>
          </View>

          {/* Body copy */}
          <Text className="text-[14px] leading-[20px] text-[#B5BAC3]">
            {shareLocationStrings.live.bodyPrefix}
            <Text className="text-[#F5F6F8]">{name}</Text>
            {shareLocationStrings.live.bodySuffix}
          </Text>

          {/* Radio options */}
          <View>
            {LIVE_DURATION_OPTIONS.map((option) => {
              const isSelected = option.value === selected;
              return (
                <Pressable
                  key={option.value}
                  onPress={() => setSelected(option.value)}
                  accessibilityRole="radio"
                  accessibilityLabel={option.label}
                  accessibilityState={{ checked: isSelected }}
                  className="flex-row items-center justify-between py-3"
                >
                  <Text className="text-[16px] text-[#F5F6F8]">
                    {option.label}
                  </Text>
                  <View
                    className={`h-[22px] w-[22px] items-center justify-center rounded-full border ${
                      isSelected
                        ? 'border-light-accent-brand bg-light-accent-brand dark:border-dark-accent-brand dark:bg-dark-accent-brand'
                        : 'border-[#717680] bg-transparent'
                    }`}
                  >
                    {isSelected ? (
                      <View className="h-2 w-2 rounded-full bg-white" />
                    ) : null}
                  </View>
                </Pressable>
              );
            })}
          </View>

          {/* Footer */}
          <View className="flex-row items-center justify-end gap-3 border-t border-light-border-subtle dark:border-dark-border-subtle pt-4">
            <Pressable
              onPress={onCancel}
              accessibilityRole="button"
              accessibilityLabel={shareLocationStrings.modal.cancel}
              className="px-3 py-1.5"
            >
              <Text className="text-[14px] text-[#F5F6F8]">
                {shareLocationStrings.modal.cancel}
              </Text>
            </Pressable>
            <Pressable
              onPress={() => onConfirm(selected)}
              accessibilityRole="button"
              accessibilityLabel={shareLocationStrings.modal.share}
              className="rounded-[8px] bg-light-accent-brand dark:bg-dark-accent-brand px-3 py-1.5"
            >
              <Text className="text-[14px] font-bold text-white">
                {shareLocationStrings.modal.share}
              </Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </RNModal>
  );
};
